import { useTranslation } from 'react-i18next';
import { ConstellationDivider } from 'shared/components/ConstellationDivider/ConstellationDivider';
import type { ShowNotes } from 'shared/data/episodes';

type ShowNotesSectionProps = {
  showNotes: ShowNotes;
};

export function ShowNotesSection({ showNotes }: ShowNotesSectionProps) {
  const { t } = useTranslation('episodeDetail');
  return (
    <div id="show-notes" className="lg:col-span-7">
      <h2 className="font-serif text-4xl mb-12 flex items-center gap-4">
        {t('showNotes.title')}
        <ConstellationDivider className="opacity-40" />
      </h2>

      <div className="space-y-10 text-on-surface-variant font-light leading-relaxed text-lg">
        <p>{showNotes.intro}</p>

        {showNotes.quote && (
          <blockquote className="bg-surface-container-lowest p-12 rounded-2xl relative border-l-4 border-tertiary/20">
            <span className="font-serif text-6xl text-tertiary/20 absolute top-4 left-6" aria-hidden="true">
              &ldquo;
            </span>
            <p className="font-serif text-2xl text-on-surface italic relative z-10 leading-snug">
              {showNotes.quote.text}
            </p>
            <footer className="mt-4 font-sans text-xs uppercase tracking-widest text-tertiary">
              — <cite>{showNotes.quote.attribution}</cite>
            </footer>
            <span className="font-serif text-6xl text-tertiary/20 absolute -bottom-2 right-6" aria-hidden="true">
              &rdquo;
            </span>
          </blockquote>
        )}

        <h3 className="font-serif text-2xl text-on-surface mt-16 mb-6">
          {t('showNotes.discussionHeading')}
        </h3>
        <ol className="space-y-6">
          {showNotes.discussionPoints.map((point, index) => (
            <li key={point.title} className="flex gap-4">
              <span className="text-tertiary font-serif italic text-2xl">
                {String(index + 1).padStart(2, '0')}.
              </span>
              <div>
                <h4 className="text-on-surface font-semibold mb-1">
                  {point.title}
                </h4>
                <p>{point.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
